import React, { useState, useEffect } from 'react';
import {
  Table, Button, Form, Input, message, Card, Space, Tag,
  Select, Row, Col, Tooltip, Popconfirm, Empty, Segmented, Progress,
  Typography, InputNumber, Pagination, DatePicker, Tabs
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined,
  DollarOutlined, FolderOutlined, SettingOutlined, ArrowUpOutlined, ArrowDownOutlined,
  WalletOutlined, RiseOutlined, FallOutlined, PieChartOutlined, AppstoreOutlined,
  BarsOutlined, TagsOutlined, CalendarOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';

// 配置axios基础URL，指向后端服务
axios.defaults.baseURL = (typeof window !== 'undefined' ? window.location.protocol + '//' + window.location.host : '') + '/api';

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

const BRAND = '#6366F1';
const fmtMoney = (n) => `¥${(Number(n) || 0).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '')}`
});

const Finance = () => {
  const [finances, setFinances] = useState([]);
  const [financeTypes, setFinanceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [editingFinance, setEditingFinance] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [selectedFinanceType, setSelectedFinanceType] = useState('all');
  const [selectedAccount, setSelectedAccount] = useState('all');
  const [dateRange, setDateRange] = useState(null);
  const [selectedYear, setSelectedYear] = useState('all');
  const [filterSummary, setFilterSummary] = useState(null);
  const [sortBy, setSortBy] = useState('recent');
  const [viewMode, setViewMode] = useState('card');
  const [activeTab, setActiveTab] = useState('finances');
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 20,
    total: 0
  });
  const [statistics, setStatistics] = useState({
    totalRecords: 0,
    totalIncome: 0,
    totalExpense: 0,
    balance: 0
  });
  const [overview, setOverview] = useState({
    monthIncome: 0, monthExpense: 0, monthBalance: 0,
    byCategory: [], byAccount: [], byMonth: []
  });
  const [form] = Form.useForm();
  const [typeForm] = Form.useForm();

  // 根据选择的收入/支出类型过滤资金类型
  const getFilteredFinanceTypes = (type) => {
    if (!type || type === 'all') {
      return financeTypes;
    }
    return financeTypes.filter(financeType => financeType.type === type);
  };

  useEffect(() => {
    fetchFinances();
    fetchStatistics();
    fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, searchText, selectedType, selectedFinanceType, selectedAccount, dateRange, sortBy]);

  useEffect(() => {
    fetchFinanceTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 监听表单中type字段的变化，强制重新渲染资金类型选择器
  const [formType, setFormType] = useState('');

  // 使用表单字段值变化监听
  const handleFormValuesChange = (changedValues, allValues) => {
    if (changedValues.type !== undefined) {
      setFormType(changedValues.type);
      // 当类型改变时，清空资金类型选择
      if (changedValues.type !== formType) {
        form.setFieldsValue({ finance_type: undefined });
      }
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await axios.get('/finance/stats', { headers: authHeaders() });
      if (response.data.success && response.data.statistics) {
        setStatistics(response.data.statistics);
      }
    } catch (error) {
      console.error('获取财务统计失败:', error);
    }
  };

  const fetchOverview = async () => {
    try {
      const response = await axios.get('/finance/stats/overview', { headers: authHeaders() });
      if (response.data.success && response.data.overview) {
        setOverview(response.data.overview);
      }
    } catch (error) {
      console.error('获取资金总览失败:', error);
    }
  };

  const fetchFinances = async () => {
    setLoading(true);
    try {
      let response = await axios.get('/finance', {
        params: {
          page: pagination.current,
          limit: pagination.pageSize,
          search: searchText,
          type: selectedType,
          finance_type: selectedFinanceType,
          account: selectedAccount,
          start_date: dateRange && dateRange[0] ? dateRange[0].format('YYYY-MM-DD') : '',
          end_date: dateRange && dateRange[1] ? dateRange[1].format('YYYY-MM-DD') : '',
          sort: sortBy
        },
        headers: authHeaders()
      });
      if (response.data.success) {
        setFinances(response.data.finances || []);
        setFilterSummary(response.data.summary || null);
        if (response.data.pagination) {
          setPagination(prev => ({
            ...prev,
            total: response.data.pagination.total || 0
          }));
        }
      } else {
        throw new Error('primary_api_failed');
      }
    } catch (error) {
      // 降级到 /api/finance/stats
      try {
        const fallback = await axios.get('/finance/stats', { headers: authHeaders() });
        if (fallback.data && fallback.data.success) {
          const list = fallback.data.finances || fallback.data.records || [];
          const total = typeof fallback.data.data === 'number' ? fallback.data.data : (list.length || 0);
          setFinances(list);
          setPagination(prev => ({ ...prev, total }));
        } else {
          throw new Error('fallback_api_failed');
        }
      } catch (e2) {
        // 未登录时不显示错误提示
        const token = localStorage.getItem('token') || (localStorage.getItem('auth-storage') ? JSON.parse(localStorage.getItem('auth-storage')).state.token : '');
        if (token) {
          message.error('获取资金记录列表失败');
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchFinanceTypes = async () => {
    try {
      const response = await axios.get('/finance/types', { headers: authHeaders() });
      if (response.data.success) {
        setFinanceTypes(response.data.types);
        return;
      }
      throw new Error('types_api_failed');
    } catch (error) {
      // 降级：从当前 finances 中推断资金类型
      setFinanceTypes(prev => {
        const names = Array.from(new Set((finances || []).map(f => f.finance_type).filter(Boolean)));
        return names.map((name, idx) => ({ id: idx + 1, type_name: name, type: 'income', description: null }));
      });
    }
  };

  const refreshAll = () => {
    fetchFinances();
    fetchStatistics();
    fetchOverview();
  };

  const handleSubmit = async () => {
    try {
      const raw = await form.validateFields();
      const values = {
        ...raw,
        occurred_date: raw.occurred_date ? dayjs(raw.occurred_date).format('YYYY-MM-DD') : null
      };

      if (editingFinance) {
        const response = await axios.put(`/finance/${editingFinance.id}`, values, { headers: authHeaders() });
        if (response.data.success) {
          message.success('资金记录更新成功');
        }
      } else {
        const response = await axios.post('/finance', values, { headers: authHeaders() });
        if (response.data.success) {
          message.success('资金记录添加成功');
        }
      }

      refreshAll();
      setModalVisible(false);
      setEditingFinance(null);
      form.resetFields();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const openAddModal = () => {
    setEditingFinance(null);
    form.resetFields();
    setFormType('');
    form.setFieldsValue({ occurred_date: dayjs() });
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingFinance(record);
    setFormType(record.type || '');
    form.setFieldsValue({
      ...record,
      occurred_date: record.occurred_date ? dayjs(record.occurred_date) : (record.created_at ? dayjs(record.created_at) : null)
    });
    setModalVisible(true);
  };

  const handleDelete = async (id) => {
    try {
      const response = await axios.delete(`/finance/${id}`, { headers: authHeaders() });
      if (response.data.success) {
        message.success('资金记录删除成功');
        refreshAll();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  const handleSearch = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  const resetFilters = () => {
    setSearchText('');
    setSelectedType('all');
    setSelectedFinanceType('all');
    setSelectedAccount('all');
    setDateRange(null);
    setSelectedYear('all');
    setSortBy('recent');
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 按年份筛选：选中年份即把区间设为该年整年（复用已验证的日期区间过滤）
  const yearOptions = (() => {
    const cy = dayjs().year();
    const arr = [];
    for (let y = cy; y >= cy - 6; y--) arr.push(y);
    return arr;
  })();
  const onYearChange = (y) => {
    setSelectedYear(y);
    setPagination(prev => ({ ...prev, current: 1 }));
    if (y === 'all') {
      setDateRange(null);
    } else {
      setDateRange([dayjs(`${y}-01-01`), dayjs(`${y}-12-31`)]);
    }
  };

  const handlePageChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
  };

  const handleTypeSubmit = async () => {
    try {
      const values = await typeForm.validateFields();

      if (editingType) {
        const response = await axios.put(`/finance/types/${editingType.id}`, values, { headers: authHeaders() });
        if (response.data.success) {
          message.success('资金类型更新成功');
        }
      } else {
        const response = await axios.post('/finance/types', values, { headers: authHeaders() });
        if (response.data.success) {
          message.success('资金类型添加成功');
        }
      }

      fetchFinanceTypes();
      setTypeModalVisible(false);
      setEditingType(null);
      typeForm.resetFields();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleTypeEdit = (record) => {
    setEditingType(record);
    typeForm.setFieldsValue(record);
    setTypeModalVisible(true);
  };

  const handleTypeDelete = async (id) => {
    try {
      const response = await axios.delete(`/finance/types/${id}`, { headers: authHeaders() });
      if (response.data.success) {
        message.success('资金类型删除成功');
        fetchFinanceTypes();
      }
    } catch (error) {
      message.error('删除失败');
    }
  };

  // 服务端已完成筛选，直接使用返回列表
  const displayFinances = finances;

  // 账户筛选项：来自总览聚合
  const accountOptions = (overview.byAccount || []).map(a => a.name).filter(n => n && n !== '未指定');

  // 支出占比
  const categoryTotal = (overview.byCategory || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);

  const renderTags = (tags) => {
    if (!tags) return null;
    return String(tags).split(/[,，]/).map(t => t.trim()).filter(Boolean).slice(0, 4).map((t, i) => (
      <span key={i} className="mod-badge mod-badge-primary">{t}</span>
    ));
  };

  const columns = [
    {
      title: '类型',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'income' ? 'green' : 'red'} icon={type === 'income' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}>
          {type === 'income' ? '收入' : '支出'}
        </Tag>
      )
    },
    {
      title: '资金类型',
      dataIndex: 'finance_type',
      key: 'finance_type',
      render: (type) => type ? <Tag color="blue">{type}</Tag> : '-'
    },
    {
      title: '账户',
      dataIndex: 'account',
      key: 'account',
      render: (a) => a ? <Tag icon={<WalletOutlined />} color="purple">{a}</Tag> : '-'
    },
    {
      title: '金额',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amount, record) => (
        <Text strong style={{ color: record.type === 'income' ? '#52c41a' : '#ff4d4f' }}>
          {record.type === 'income' ? '+' : '-'}{fmtMoney(amount)}
        </Text>
      )
    },
    {
      title: '标签',
      dataIndex: 'tags',
      key: 'tags',
      render: (tags) => tags ? <div className="mod-badges">{renderTags(tags)}</div> : '-'
    },
    {
      title: '备注',
      dataIndex: 'remark',
      key: 'remark',
      ellipsis: true,
      render: (remark) => remark || '-'
    },
    {
      title: '日期',
      key: 'date',
      render: (_, r) => dayjs(r.occurred_date || r.created_at).format('YYYY-MM-DD')
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space>
          <Tooltip title="编辑">
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)} />
          </Tooltip>
          <Popconfirm
            title="确定要删除这个资金记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Tooltip title="删除">
              <Button type="link" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const statCards = [
    { key: 'income', label: '本月收入', value: overview.monthIncome, icon: <RiseOutlined />, color: '#52c41a', filter: 'income' },
    { key: 'expense', label: '本月支出', value: overview.monthExpense, icon: <FallOutlined />, color: '#ff4d4f', filter: 'expense', danger: true },
    { key: 'mbalance', label: '本月结余', value: overview.monthBalance, icon: <WalletOutlined />, color: BRAND },
    { key: 'balance', label: '累计结余', value: statistics.balance, icon: <DollarOutlined />, color: statistics.balance >= 0 ? '#52c41a' : '#ff4d4f', sub: `共 ${statistics.totalRecords} 条记录` }
  ];

  return (
    <div className="mod-page">
      {/* 页头 */}
      <div className="mod-header">
        <div>
          <div className="mod-header-title">资金管理</div>
          <div className="mod-header-subtitle">记录每一笔收支，掌握账户余额与支出结构</div>
        </div>
        <Space wrap>
          <Button
            icon={<SettingOutlined />}
            onClick={() => {
              setActiveTab('types');
              setEditingType(null);
              typeForm.resetFields();
              setTypeModalVisible(true);
            }}
          >
            管理资金类型
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>
            添加资金记录
          </Button>
        </Space>
      </div>

      {/* 统计卡 */}
      <div className="mod-stats">
        {statCards.map(c => (
          <div
            key={c.key}
            className={`mod-stat-card${c.filter ? ' clickable' : ''}${c.danger ? ' danger' : ''}`}
            onClick={c.filter ? () => {
              setSelectedType(prev => prev === c.filter ? 'all' : c.filter);
              setPagination(prev => ({ ...prev, current: 1 }));
            } : undefined}
            style={c.filter && selectedType === c.filter ? { outline: `2px solid ${c.color}` } : undefined}
          >
            <div className="mod-stat-icon" style={{ color: c.color }}>{c.icon}</div>
            <div className="mod-stat-value" style={{ color: c.color }}>{fmtMoney(c.value)}</div>
            <div className="mod-stat-label">{c.sub || c.label}</div>
          </div>
        ))}
      </div>

      <Tabs activeKey={activeTab} onChange={setActiveTab}>
        <TabPane tab="资金记录" key="finances">
          {/* 支出占比 */}
          {(overview.byCategory || []).length > 0 && (
            <Card className="mod-card" style={{ marginBottom: 16 }} bodyStyle={{ padding: 16 }}>
              <div className="mod-section-title"><PieChartOutlined style={{ color: BRAND, marginRight: 8 }} />支出占比</div>
              <Row gutter={[16, 8]} style={{ marginTop: 8 }}>
                {overview.byCategory.slice(0, 6).map((c, i) => {
                  const pct = categoryTotal > 0 ? Math.round((c.amount / categoryTotal) * 100) : 0;
                  return (
                    <Col xs={24} sm={12} md={8} key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 2 }}>
                        <span>{c.name}</span>
                        <span style={{ color: '#ff4d4f' }}>{fmtMoney(c.amount)}（{pct}%）</span>
                      </div>
                      <Progress percent={pct} showInfo={false} strokeColor={BRAND} size="small" />
                    </Col>
                  );
                })}
              </Row>
            </Card>
          )}

          {/* 工具栏 */}
          <Card className="mod-toolbar" bodyStyle={{ padding: 16 }}>
            <div className="mod-toolbar-row">
              <Row gutter={[12, 12]} style={{ width: '100%' }} align="middle">
                <Col xs={24} sm={12} md={8} lg={6}>
                  <Input.Search
                    placeholder="搜索备注 / 类型 / 账户 / 标签"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    onSearch={handleSearch}
                    allowClear
                    enterButton
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Select value={selectedType} onChange={(v) => { setSelectedType(v); setPagination(p => ({ ...p, current: 1 })); }} style={{ width: '100%' }}>
                    <Option value="all">全部收支</Option>
                    <Option value="income">收入</Option>
                    <Option value="expense">支出</Option>
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={4}>
                  <Select value={selectedFinanceType} onChange={(v) => { setSelectedFinanceType(v); setPagination(p => ({ ...p, current: 1 })); }} style={{ width: '100%' }} showSearch optionFilterProp="children">
                    <Option value="all">全部分类</Option>
                    {financeTypes.map(type => (
                      <Option key={type.id} value={type.type_name}>{type.type_name}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Select value={selectedAccount} onChange={(v) => { setSelectedAccount(v); setPagination(p => ({ ...p, current: 1 })); }} style={{ width: '100%' }} showSearch optionFilterProp="children" placeholder="账户">
                    <Option value="all">全部账户</Option>
                    {accountOptions.map((a, i) => (
                      <Option key={i} value={a}>{a}</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Select value={selectedYear} onChange={onYearChange} style={{ width: '100%' }} placeholder="年份">
                    <Option value="all">全部年份</Option>
                    {yearOptions.map((y) => (
                      <Option key={y} value={y}>{y}年</Option>
                    ))}
                  </Select>
                </Col>
                <Col xs={24} sm={12} md={8} lg={5}>
                  <RangePicker
                    style={{ width: '100%' }}
                    value={dateRange}
                    onChange={(v) => { setDateRange(v); setSelectedYear('all'); setPagination(p => ({ ...p, current: 1 })); }}
                    placeholder={['开始日期', '结束日期']}
                  />
                </Col>
                <Col xs={12} sm={6} md={4} lg={3}>
                  <Select value={sortBy} onChange={setSortBy} style={{ width: '100%' }}>
                    <Option value="recent">最新优先</Option>
                    <Option value="oldest">最早优先</Option>
                    <Option value="amount_desc">金额从高到低</Option>
                    <Option value="amount_asc">金额从低到高</Option>
                  </Select>
                </Col>
                <Col xs={12} sm={6} md={4} lg={2}>
                  <Button onClick={resetFilters} block>重置</Button>
                </Col>
                <Col xs={24} sm={24} md={24} lg={5} style={{ textAlign: 'right' }}>
                  <Segmented
                    value={viewMode}
                    onChange={setViewMode}
                    options={[
                      { value: 'card', icon: <AppstoreOutlined /> },
                      { value: 'table', icon: <BarsOutlined /> }
                    ]}
                  />
                </Col>
              </Row>
            </div>
          </Card>

          {/* 本次筛选合计（跟随日期区间/年份等所有筛选条件） */}
          {filterSummary && (dateRange || selectedYear !== 'all' || selectedType !== 'all' || selectedFinanceType !== 'all' || selectedAccount !== 'all' || searchText) && (
            <Card className="mod-card" bodyStyle={{ padding: '10px 16px' }} style={{ marginBottom: 12 }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 18, alignItems: 'center', fontSize: 14 }}>
                <span style={{ color: '#8c8c8c' }}>筛选合计（{filterSummary.count} 笔）</span>
                <span>收入 <Text strong style={{ color: '#52c41a' }}>¥{Number(filterSummary.income).toLocaleString()}</Text></span>
                <span>支出 <Text strong style={{ color: '#ff4d4f' }}>¥{Number(filterSummary.expense).toLocaleString()}</Text></span>
                <span>结余 <Text strong style={{ color: filterSummary.balance >= 0 ? '#52c41a' : '#ff4d4f' }}>¥{Number(filterSummary.balance).toLocaleString()}</Text></span>
              </div>
            </Card>
          )}

          {/* 列表主体 */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <Text type="secondary">加载中...</Text>
            </div>
          ) : displayFinances.length === 0 ? (
            <Card className="mod-card" bodyStyle={{ padding: 48 }}>
              <Empty description="暂无资金记录">
                <Button type="primary" icon={<PlusOutlined />} onClick={openAddModal}>添加资金记录</Button>
              </Empty>
            </Card>
          ) : viewMode === 'table' ? (
            <Card className="mod-card" bodyStyle={{ padding: 0 }}>
              <Table
                rowKey="id"
                columns={columns}
                dataSource={displayFinances}
                pagination={false}
                scroll={{ x: 'max-content' }}
              />
            </Card>
          ) : (
            <Row gutter={[16, 16]}>
              {displayFinances.map((finance) => (
                <Col xs={24} sm={12} md={8} lg={6} key={finance.id}>
                  <div className={`mod-card${finance.type === 'expense' ? ' danger' : ''}`}>
                    <div className="mod-card-head">
                      <div className="mod-card-icon" style={{ color: finance.type === 'income' ? '#52c41a' : '#ff4d4f' }}>
                        {finance.type === 'income' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="mod-card-title">{finance.finance_type || (finance.type === 'income' ? '收入' : '支出')}</div>
                        <div className="mod-card-sub">
                          <CalendarOutlined style={{ marginRight: 4 }} />
                          {dayjs(finance.occurred_date || finance.created_at).format('YYYY-MM-DD')}
                        </div>
                      </div>
                      <div style={{ fontSize: 15, fontWeight: 700, color: finance.type === 'income' ? '#52c41a' : '#ff4d4f', whiteSpace: 'nowrap' }}>
                        {finance.type === 'income' ? '+' : '-'}{fmtMoney(finance.amount)}
                      </div>
                    </div>
                    <div className="mod-card-body">
                      <div className="mod-badges" style={{ marginBottom: 8 }}>
                        <span className={`mod-badge ${finance.type === 'income' ? 'mod-badge-success' : 'mod-badge-danger'}`}>
                          {finance.type === 'income' ? '收入' : '支出'}
                        </span>
                        {finance.account && <span className="mod-badge mod-badge-primary"><WalletOutlined style={{ marginRight: 4 }} />{finance.account}</span>}
                        {renderTags(finance.tags)}
                      </div>
                      {finance.remark && (
                        <Text type="secondary" style={{ fontSize: 13, display: 'block' }} ellipsis={{ tooltip: finance.remark }}>
                          {finance.remark}
                        </Text>
                      )}
                    </div>
                    <div className="mod-card-actions">
                      <Button type="text" icon={<EditOutlined />} onClick={() => handleEdit(finance)}>编辑</Button>
                      <Popconfirm
                        title="确定要删除这个资金记录吗？"
                        onConfirm={() => handleDelete(finance.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button type="text" danger icon={<DeleteOutlined />}>删除</Button>
                      </Popconfirm>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          {/* 分页组件 */}
          {pagination.total > 0 && (
            <div style={{ textAlign: 'center', marginTop: 24 }}>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger
                showQuickJumper
                showTotal={(total, range) => `第 ${range[0]}-${range[1]} 条，共 ${total} 条`}
                pageSizeOptions={['10', '20', '50', '100']}
              />
            </div>
          )}
        </TabPane>

        <TabPane tab="资金类型" key="types">
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => {
                setEditingType(null);
                typeForm.resetFields();
                setTypeModalVisible(true);
              }}
            >
              添加资金类型
            </Button>
          </div>

          {financeTypes.length === 0 ? (
            <Card className="mod-card" bodyStyle={{ padding: 48 }}>
              <Empty description="暂无资金类型" image={<FolderOutlined style={{ fontSize: 48, color: '#d9d9d9' }} />} />
            </Card>
          ) : (
            <Row gutter={[16, 16]}>
              {financeTypes.map((type) => (
                <Col xs={24} sm={12} md={8} lg={6} key={type.id}>
                  <div className="mod-card">
                    <div className="mod-card-head">
                      <div className="mod-card-icon" style={{ color: type.type === 'income' ? '#52c41a' : '#ff4d4f' }}>
                        <TagsOutlined />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="mod-card-title">{type.type_name}</div>
                        <div className="mod-card-sub">
                          <span className={`mod-badge ${type.type === 'income' ? 'mod-badge-success' : 'mod-badge-danger'}`}>
                            {type.type === 'income' ? '收入' : '支出'}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="mod-card-body">
                      <Text type="secondary" style={{ fontSize: 13 }}>{type.description || '无描述'}</Text>
                    </div>
                    <div className="mod-card-actions">
                      <Button type="text" icon={<EditOutlined />} onClick={() => handleTypeEdit(type)}>编辑</Button>
                      <Popconfirm
                        title="确定要删除这个资金类型吗？"
                        onConfirm={() => handleTypeDelete(type.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button type="text" danger icon={<DeleteOutlined />}>删除</Button>
                      </Popconfirm>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </TabPane>
      </Tabs>

      {/* 资金记录添加/编辑模态框 */}
      {modalVisible && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: 16
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 600, maxHeight: '86vh', overflow: 'auto',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)'
          }}>
            <h3 style={{ margin: '0 0 14px 0', fontSize: 15, fontWeight: 600 }}>
              {editingFinance ? '编辑资金记录' : '添加资金记录'}
            </h3>
            <Form form={form} layout="vertical" onFinish={handleSubmit} onValuesChange={handleFormValuesChange}>
              <Row gutter={16}>
                <Col xs={24} sm={12}>
                  <Form.Item name="type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
                    <Select
                      placeholder="选择类型"
                      onChange={(value) => {
                        form.setFieldsValue({ finance_type: undefined });
                        setFormType(value);
                      }}
                    >
                      <Option value="income">收入</Option>
                      <Option value="expense">支出</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="finance_type" label="资金类型">
                    <Select placeholder="选择资金类型" disabled={!formType} allowClear>
                      {getFilteredFinanceTypes(formType).map(type => (
                        <Option key={type.id} value={type.type_name}>{type.type_name}</Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="amount" label="金额" rules={[{ required: true, message: '请输入金额' }]}>
                    <InputNumber placeholder="请输入金额" min={0} precision={2} style={{ width: '100%' }} prefix="¥" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="occurred_date" label="记账日期">
                    <DatePicker style={{ width: '100%' }} placeholder="选择日期" />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="account" label="账户">
                    <Input placeholder="如 现金 / 银行卡 / 支付宝 / 微信" prefix={<WalletOutlined />} />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={12}>
                  <Form.Item name="tags" label="标签">
                    <Input placeholder="多个标签用逗号分隔" prefix={<TagsOutlined />} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item name="remark" label="备注说明">
                    <TextArea rows={3} placeholder="请输入备注说明" />
                  </Form.Item>
                </Col>
              </Row>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <Button onClick={() => { setModalVisible(false); setEditingFinance(null); form.resetFields(); }}>取消</Button>
                <Button type="primary" htmlType="submit">{editingFinance ? '更新' : '添加'}</Button>
              </div>
            </Form>
          </div>
        </div>
      )}

      {/* 资金类型添加/编辑模态框 */}
      {typeModalVisible && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
          justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: 16
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: 16, padding: 24,
            width: '100%', maxWidth: 500, maxHeight: '86vh', overflow: 'auto',
            boxShadow: '0 12px 40px rgba(0, 0, 0, 0.18)'
          }}>
            <h3 style={{ margin: '0 0 14px 0', fontSize: 15, fontWeight: 600 }}>
              {editingType ? '编辑资金类型' : '添加资金类型'}
            </h3>
            <Form form={typeForm} layout="vertical" onFinish={handleTypeSubmit}>
              <Form.Item name="type_name" label="类型名称" rules={[{ required: true, message: '请输入类型名称' }]}>
                <Input placeholder="请输入资金类型名称" />
              </Form.Item>

              <Form.Item name="type" label="类型" rules={[{ required: true, message: '请选择类型' }]}>
                <Select placeholder="请选择类型">
                  <Option value="income">收入</Option>
                  <Option value="expense">支出</Option>
                </Select>
              </Form.Item>

              <Form.Item name="description" label="类型描述">
                <TextArea rows={3} placeholder="请输入类型描述" />
              </Form.Item>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
                <Button onClick={() => { setTypeModalVisible(false); setEditingType(null); typeForm.resetFields(); }}>取消</Button>
                <Button type="primary" htmlType="submit">{editingType ? '更新' : '添加'}</Button>
              </div>
            </Form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Finance;
